import React from 'react';
import styled from 'styled-components';
import { useInView } from 'react-intersection-observer';

const AboutSection = styled.section`
  background-color: ${props => props.theme.colors.dark};
  position: relative;
  overflow: hidden;
`;

const Container = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;

  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
    text-align: center;
  }
`;

const ImageContainer = styled.div`
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: -20px;
    left: -20px;
    width: 100%;
    height: 100%;
    border: 1px solid ${props => props.theme.colors.primary};
    border-radius: 8px;
    z-index: 1;
  }

  img {
    position: relative;
    z-index: 2;
    border-radius: 8px;
    width: 100%;
    height: auto;
  }
`;

const Content = styled.div`
  .section-title {
    font-size: clamp(2rem, 4vw, 3rem);
    margin-bottom: 2rem;

    .highlight {
      color: ${props => props.theme.colors.primary};
    }
  }

  .description {
    color: ${props => props.theme.colors.gray};
    margin-bottom: 2rem;
    font-size: 1.1rem;
    line-height: 1.8;
  }
`;

const Certification = styled.a`
  display: flex;
  align-items: center;
  gap: 1.25rem;
  padding: 1.5rem;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 12px;
  background: ${props => props.theme.colors.background};
  margin-bottom: 3rem;
  transition: border-color 0.3s ease, transform 0.3s ease;

  img {
    width: 72px;
    height: 72px;
    flex-shrink: 0;
  }

  .cert-name {
    color: ${props => props.theme.colors.text};
    font-size: 1.15rem;
    font-weight: 600;
    margin-bottom: 0.25rem;
  }

  .cert-issuer {
    color: ${props => props.theme.colors.gray};
    font-size: 0.95rem;
  }

  .cert-link {
    color: ${props => props.theme.colors.primary};
    font-size: 0.9rem;
    margin-top: 0.35rem;
  }

  &:hover {
    border-color: ${props => props.theme.colors.primary};
    transform: translateY(-3px);
  }

  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    text-align: left;
  }
`;

const TutorSection = styled.div`
  margin-bottom: 3rem;

  .tutor-title {
    font-size: 1.5rem;
    color: ${props => props.theme.colors.primary};
    margin-bottom: 1.5rem;
  }

  .tutor-description {
    color: ${props => props.theme.colors.text};
    font-size: 1.1rem;
    line-height: 1.8;
    margin-bottom: 1.5rem;
  }

  .tutor-stats {
    display: flex;
    align-items: center;
    gap: 1rem;

    .number {
      font-size: 2.5rem;
      font-weight: 700;
      color: ${props => props.theme.colors.primary};
    }

    .label {
      color: ${props => props.theme.colors.gray};
      font-size: 1.1rem;
      line-height: 1.4;
    }
  }

  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    .tutor-stats {
      justify-content: center;
    }
  }
`;

const Education = styled.div`
  margin-bottom: 3rem;

  .education-title {
    font-size: 1.5rem;
    color: ${props => props.theme.colors.primary};
    margin-bottom: 1.5rem;
  }

  .education-item {
    margin-bottom: 1.5rem;

    .year {
      color: ${props => props.theme.colors.primary};
      font-weight: 600;
      margin-bottom: 0.5rem;
    }

    .degree {
      color: ${props => props.theme.colors.text};
      font-size: 1.1rem;
      margin-bottom: 0.25rem;
    }

    .university {
      color: ${props => props.theme.colors.gray};
      font-size: 1rem;
    }
  }
`;

const Stats = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  margin-top: 3rem;

  .stat-item {
    .number {
      font-size: 2.5rem;
      font-weight: 700;
      color: ${props => props.theme.colors.primary};
      margin-bottom: 0.5rem;
    }

    .label {
      color: ${props => props.theme.colors.gray};
      font-size: 0.9rem;
    }
  }
`;

const CREDLY_URL = 'https://www.credly.com/badges/c0dece68-3945-4488-bd8a-fc84b8554c96';
const CREDLY_BADGE_IMG = 'https://images.credly.com/size/340x340/images/f2040db3-3904-4240-8966-e87b1510bea0/blob';

const About = () => {
  const { ref, inView } = useInView({
    threshold: 0.3,
    triggerOnce: true,
  });

  return (
    <AboutSection id="about">
      <div className="container">
        <Container ref={ref} className={inView ? 'reveal active' : 'reveal'}>
          <ImageContainer>
            <img src="/images/profile.jpg" alt="Hayk Avdalyan" />
          </ImageContainer>
          <Content>
            <h2 className="section-title">
              AI expertise, grounded in <span className="highlight">real engineering</span>
            </h2>
            <p className="description">
              I'm a Software Engineering Manager and AI consultant with over 12 years in
              software engineering and technical leadership. I work with businesses to move beyond AI hype — assessing where AI
              genuinely creates value, then integrating AI tools and agents directly into
              day-to-day operations. Because I've spent my career building and leading
              engineering teams, my recommendations don't stop at slide decks: I implement
              them hands-on, end to end.
            </p>
            <Certification href={CREDLY_URL} target="_blank" rel="noopener noreferrer">
              <img src={CREDLY_BADGE_IMG} alt="Claude Certified Architect - Foundations badge" />
              <div>
                <div className="cert-name">Claude Certified Architect – Foundations</div>
                <div className="cert-issuer">
                  Issued by Anthropic — designing and building production-grade AI applications and agents
                </div>
                <div className="cert-link">Verify on Credly →</div>
              </div>
            </Certification>
            <TutorSection>
              <h3 className="tutor-title">Java Programming Tutor</h3>
              <p className="tutor-description">
                With 8 years of experience as a Java Programming language tutor, I've helped
                numerous students and professionals master Java development and advance their careers.
              </p>
              <div className="tutor-stats">
                <span className="number">150+</span>
                <span className="label">Successful students working<br />in different companies worldwide</span>
              </div>
            </TutorSection>
            <Education>
              <h3 className="education-title">Education</h3>
              <div className="education-item">
                <div className="year">2014 – 2016</div>
                <div className="degree">Master of Applied Mathematics and Informatics</div>
                <div className="university">Yerevan State University</div>
              </div>
              <div className="education-item">
                <div className="year">2010 – 2014</div>
                <div className="degree">Bachelor of Applied Mathematics and Informatics</div>
                <div className="university">Yerevan State University</div>
              </div>
            </Education>
            <Stats>
              <div className="stat-item">
                <div className="number">12+</div>
                <div className="label">Years in Engineering</div>
              </div>
              <div className="stat-item">
                <div className="number">5+</div>
                <div className="label">Years Leadership</div>
              </div>
              <div className="stat-item">
                <div className="number">100+</div>
                <div className="label">Projects Delivered</div>
              </div>
            </Stats>
          </Content>
        </Container>
      </div>
    </AboutSection>
  );
};

export default About;
